import { useEffect, useRef, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import {
  MessageCircle, X, Send, Mic, StopCircle, Image as ImageIcon, ChevronLeft,
  Users, Search, User, Stethoscope, Building2, ShieldCheck, Play,
} from "lucide-react";

const ROLE_BADGE = {
  super_admin: { label: "Platform", color: "#004a99", Icon: ShieldCheck },
  clinic_admin: { label: "Admin", color: "#7c3aed", Icon: Building2 },
  doctor: { label: "Doctor", color: "#0d9488", Icon: Stethoscope },
  patient: { label: "Patient", color: "#f37021", Icon: User },
};

const fmtRel = (iso) => {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  return new Date(iso).toLocaleDateString();
};
const fmtTime = (iso) => new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

// Bottom-right floating launcher + slide-over drawer
export default function MessageCenter({ openThreadId, onConsumeOpen }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [view, setView] = useState("threads"); // threads | chat | contacts
  const [threads, setThreads] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [active, setActive] = useState(null); // {id, other_id, other_name, other_role}
  const [unread, setUnread] = useState(0);
  const [q, setQ] = useState("");

  const refresh = useCallback(async () => {
    try {
      const r = await api.get("/inbox/threads");
      setThreads(r.data || []);
    } catch { /* noop */ }
  }, []);

  const poll = useCallback(async () => {
    try {
      const r = await api.get("/inbox/poll");
      setUnread(r.data.messages_unread || 0);
    } catch { /* noop */ }
  }, []);

  useEffect(() => {
    poll();
    const t = setInterval(poll, 8000);
    return () => clearInterval(t);
  }, [poll]);

  useEffect(() => { if (open) refresh(); }, [open, refresh]);

  // External trigger (from notification → open specific thread)
  useEffect(() => {
    if (openThreadId) {
      setOpen(true);
      (async () => {
        await refresh();
        const r = await api.get("/inbox/threads");
        const t = (r.data || []).find((x) => x.id === openThreadId);
        if (t) { setActive(t); setView("chat"); }
        onConsumeOpen?.();
      })();
    }
  }, [openThreadId, refresh, onConsumeOpen]);

  const openChatWith = async (contact) => {
    // send a "warm-up" empty thread (server creates on first message); we
    // just instantiate a local thread object and switch the view
    setActive({
      id: null, other_id: contact.user_id, other_name: contact.name,
      other_role: contact.role, clinic_id: contact.clinic_id,
    });
    setView("chat");
  };

  const openContacts = async () => {
    try {
      const r = await api.get("/inbox/contacts");
      setContacts(r.data || []);
      setView("contacts");
    } catch { toast.error("Failed to load contacts"); }
  };

  const filteredContacts = contacts.filter((c) =>
    !q || c.name.toLowerCase().includes(q.toLowerCase()) || (c.sub_label || "").toLowerCase().includes(q.toLowerCase())
  );

  const onSent = async () => { await refresh(); await poll(); };

  return (
    <>
      {/* Floating launcher */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-5 right-5 z-40 h-14 w-14 rounded-full shadow-2xl flex items-center justify-center transition-transform hover:scale-110"
          style={{ background: "linear-gradient(135deg,#f37021,#ff8e3c)" }}
          data-testid="message-center-launcher"
          title="Messages"
        >
          <MessageCircle className="h-6 w-6 text-white" />
          {unread > 0 && (
            <span className="absolute -top-1 -right-1 min-w-[22px] h-[22px] px-1.5 rounded-full bg-white text-[#f37021] text-xs font-bold flex items-center justify-center shadow"
                  data-testid="message-center-unread">
              {unread > 99 ? "99+" : unread}
            </span>
          )}
        </button>
      )}

      {/* Drawer */}
      {open && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-end pointer-events-none" data-testid="message-center-drawer">
          <div className="absolute inset-0 bg-black/30 pointer-events-auto" onClick={() => setOpen(false)} />
          <div className="relative bg-white w-full sm:w-[420px] h-[86vh] sm:h-[640px] sm:mr-5 sm:rounded-2xl shadow-2xl overflow-hidden flex flex-col pointer-events-auto animate-slide-up">
            {/* Header */}
            <div className="flex items-center gap-2 px-4 py-3 text-white" style={{ background: "linear-gradient(135deg,#004a99,#0066cc)" }}>
              {view !== "threads" && (
                <button onClick={() => setView("threads")} className="p-1 hover:bg-white/15 rounded" data-testid="msg-back">
                  <ChevronLeft className="h-5 w-5" />
                </button>
              )}
              <div className="flex-1 min-w-0">
                {view === "threads" && <div className="font-heading font-bold">Messages</div>}
                {view === "contacts" && <div className="font-heading font-bold">New conversation</div>}
                {view === "chat" && active && (
                  <div className="flex items-center gap-2">
                    <RoleAvatar role={active.other_role} size={32} />
                    <div className="min-w-0">
                      <div className="font-bold text-sm leading-tight truncate">{active.other_name}</div>
                      <div className="text-[11px] text-white/75 leading-tight">{ROLE_BADGE[active.other_role]?.label || active.other_role}</div>
                    </div>
                  </div>
                )}
              </div>
              {view === "threads" && (
                <button onClick={openContacts} className="p-1.5 hover:bg-white/15 rounded" title="New conversation" data-testid="msg-new-thread">
                  <Users className="h-4 w-4" />
                </button>
              )}
              <button onClick={() => setOpen(false)} className="p-1.5 hover:bg-white/15 rounded" data-testid="msg-close">
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-hidden flex flex-col">
              {view === "threads" && (
                <ThreadsList threads={threads} onPick={(t) => { setActive(t); setView("chat"); }} onNew={openContacts} />
              )}

              {view === "contacts" && (
                <div className="flex-1 overflow-y-auto">
                  <div className="p-3 sticky top-0 bg-white border-b">
                    <div className="relative">
                      <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                      <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search contacts…"
                             className="w-full pl-9 pr-3 py-2 rounded-lg bg-slate-100 text-sm focus:outline-none focus:ring-2 focus:ring-[#004a99]/30"
                             data-testid="contact-search" />
                    </div>
                  </div>
                  {filteredContacts.length === 0 && (
                    <div className="p-6 text-center text-sm text-slate-500">No contacts available.</div>
                  )}
                  {filteredContacts.map((c) => (
                    <button key={c.user_id} onClick={() => openChatWith(c)}
                            className="w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-50 text-left border-b border-slate-100"
                            data-testid={`contact-${c.user_id}`}>
                      <RoleAvatar role={c.role} size={36} />
                      <div className="min-w-0 flex-1">
                        <div className="font-semibold text-sm text-slate-900 truncate">{c.name}</div>
                        <div className="text-xs text-slate-500 truncate">{c.sub_label || ROLE_BADGE[c.role]?.label}</div>
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {view === "chat" && active && (
                <ChatView me={user} active={active} onSent={onSent} />
              )}
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes slide-up { from { transform: translateY(20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
        .animate-slide-up { animation: slide-up .25s cubic-bezier(.2,.8,.2,1); }
      `}</style>
    </>
  );
}

function RoleAvatar({ role, size = 36, name }) {
  const r = ROLE_BADGE[role] || ROLE_BADGE.patient;
  const Icon = r.Icon;
  const initial = (name || "").trim().charAt(0).toUpperCase();
  return (
    <div className="rounded-full flex items-center justify-center flex-shrink-0 text-white font-bold"
         style={{ height: size, width: size, background: r.color, fontSize: size * 0.4 }}>
      {initial || <Icon className="h-4 w-4" />}
    </div>
  );
}

function ThreadsList({ threads, onPick, onNew }) {
  if (!threads.length) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
        <MessageCircle className="h-10 w-10 text-slate-300 mb-3" />
        <div className="font-semibold text-slate-700">No conversations yet</div>
        <div className="text-xs text-slate-500 mt-1 mb-4">Start chatting with your team or care circle.</div>
        <button onClick={onNew} className="px-4 py-2 rounded-lg bg-[#f37021] hover:bg-[#e6661c] text-white text-sm font-semibold"
                data-testid="msg-empty-new">
          Start a conversation
        </button>
      </div>
    );
  }
  return (
    <div className="flex-1 overflow-y-auto" data-testid="threads-list">
      {threads.map((t) => (
        <button key={t.id} onClick={() => onPick(t)}
                className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-50 text-left border-b border-slate-100 ${t.unread_count ? "bg-[#fff7f0]" : ""}`}
                data-testid="thread-item" data-thread-id={t.id}>
          <div className="relative">
            <RoleAvatar role={t.other_role} name={t.other_name} size={40} />
            {t.unread_count > 0 && (
              <span className="absolute -top-1 -right-1 h-5 min-w-[20px] px-1 rounded-full bg-[#f37021] text-white text-[10px] font-bold flex items-center justify-center ring-2 ring-white">
                {t.unread_count > 9 ? "9+" : t.unread_count}
              </span>
            )}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center justify-between gap-2">
              <div className={`text-sm truncate ${t.unread_count ? "font-bold text-slate-900" : "font-semibold text-slate-700"}`}>{t.other_name}</div>
              <span className="text-[10px] text-slate-400 flex-shrink-0">{fmtRel(t.last_message_at)}</span>
            </div>
            <div className="text-xs text-slate-500 truncate">{t.last_message_preview || "—"}</div>
          </div>
        </button>
      ))}
    </div>
  );
}

function ChatView({ me, active, onSent }) {
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [recording, setRecording] = useState(false);
  const [recordSec, setRecordSec] = useState(0);
  const recorderRef = useRef(null);
  const recordTimerRef = useRef(null);
  const fileRef = useRef(null);
  const scrollRef = useRef(null);
  const pollRef = useRef(null);

  const loadMessages = useCallback(async () => {
    if (!active?.id) { setMessages([]); return; }
    try {
      const r = await api.get(`/inbox/threads/${active.id}/messages`);
      setMessages(r.data?.messages || []);
      await api.post(`/inbox/threads/${active.id}/read`);
    } catch { /* noop */ }
  }, [active?.id]);

  useEffect(() => {
    loadMessages();
    if (active?.id) {
      pollRef.current = setInterval(loadMessages, 6000);
    }
    return () => clearInterval(pollRef.current);
  }, [loadMessages, active?.id]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages.length]);

  const send = async (payload) => {
    setSending(true);
    try {
      const body = {
        recipient_id: active.other_id,
        clinic_id: active.clinic_id || me?.clinic_id || null,
        ...payload,
      };
      const r = await api.post("/inbox/messages", body);
      // If this was the first message, hydrate thread id so polling works
      const tid = r.data?.thread_id;
      if (!active.id && tid) {
        setActive((a) => ({ ...a, id: tid }));
        // append the freshly sent message immediately
        setMessages((xs) => [...xs, r.data]);
      } else {
        await loadMessages();
      }
      setText("");
      onSent?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send");
    } finally { setSending(false); }
  };

  const sendText = () => {
    const v = text.trim();
    if (!v) return;
    send({ body: v, type: "text" });
  };

  const onImage = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 4 * 1024 * 1024) { toast.error("Image must be under 4 MB"); return; }
    const b64 = await new Promise((res, rej) => {
      const r = new FileReader();
      r.onload = () => res(r.result);
      r.onerror = rej;
      r.readAsDataURL(f);
    });
    send({ body: "", type: "image", media_base64: b64, media_mime: f.type });
    e.target.value = "";
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      const chunks = [];
      mr.ondataavailable = (ev) => chunks.push(ev.data);
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunks, { type: "audio/webm" });
        if (blob.size > 4 * 1024 * 1024) { toast.error("Voice note too large"); return; }
        const b64 = await new Promise((res) => {
          const r = new FileReader();
          r.onload = () => res(r.result);
          r.readAsDataURL(blob);
        });
        send({ body: "", type: "voice", media_base64: b64, media_mime: "audio/webm" });
      };
      mr.start();
      recorderRef.current = mr;
      setRecording(true);
      setRecordSec(0);
      recordTimerRef.current = setInterval(() => setRecordSec((s) => s + 1), 1000);
    } catch (e) {
      toast.error("Microphone permission denied");
    }
  };

  const stopRecording = () => {
    if (recorderRef.current) recorderRef.current.stop();
    clearInterval(recordTimerRef.current);
    setRecording(false);
  };

  return (
    <div className="flex-1 flex flex-col bg-slate-50">
      {/* Message list */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 py-4 space-y-2" data-testid="chat-messages">
        {messages.length === 0 && (
          <div className="text-center text-xs text-slate-400 py-8">
            Start the conversation — say hello 👋
          </div>
        )}
        {messages.map((m) => {
          const mine = m.sender_id === me?.id;
          return (
            <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[78%] rounded-2xl px-3 py-2 shadow-sm ${mine ? "bg-[#004a99] text-white rounded-br-sm" : "bg-white text-slate-900 rounded-bl-sm border"}`}
                   data-testid={`msg-${m.id}`}>
                {m.type === "text" && <div className="text-sm whitespace-pre-wrap break-words">{m.body}</div>}
                {m.type === "image" && (
                  <img src={m.media_base64} alt="img" className="rounded-lg max-h-60 object-contain block" />
                )}
                {m.type === "voice" && (
                  <audio controls src={m.media_base64} className="max-w-full" style={{ height: 36 }} />
                )}
                <div className={`text-[10px] mt-1 text-right ${mine ? "text-white/70" : "text-slate-400"}`}>{fmtTime(m.created_at)}</div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Composer */}
      <div className="border-t bg-white px-3 py-2.5 flex items-end gap-2">
        <input ref={fileRef} type="file" accept="image/*" hidden onChange={onImage} data-testid="msg-image-input" />
        <button onClick={() => fileRef.current?.click()} disabled={sending || recording}
                className="p-2 rounded-lg hover:bg-slate-100 text-slate-600 flex-shrink-0" title="Send image"
                data-testid="msg-image-btn">
          <ImageIcon className="h-5 w-5" />
        </button>

        {!recording ? (
          <button onClick={startRecording} disabled={sending}
                  className="p-2 rounded-lg hover:bg-slate-100 text-slate-600 flex-shrink-0" title="Voice note"
                  data-testid="msg-voice-btn">
            <Mic className="h-5 w-5" />
          </button>
        ) : (
          <button onClick={stopRecording}
                  className="px-3 h-9 rounded-lg bg-red-50 text-red-700 flex items-center gap-1.5 flex-shrink-0 animate-pulse"
                  data-testid="msg-voice-stop">
            <StopCircle className="h-4 w-4" />
            <span className="text-xs font-mono">{String(Math.floor(recordSec / 60)).padStart(2, "0")}:{String(recordSec % 60).padStart(2, "0")}</span>
          </button>
        )}

        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendText(); } }}
          rows={1}
          placeholder={recording ? "Recording…" : "Type a message"}
          disabled={sending || recording}
          className="flex-1 resize-none rounded-2xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#004a99]/30 max-h-24"
          data-testid="msg-text-input"
        />
        <button onClick={sendText} disabled={!text.trim() || sending}
                className="h-9 w-9 rounded-full flex items-center justify-center text-white disabled:opacity-40 flex-shrink-0 transition"
                style={{ background: "#f37021" }}
                data-testid="msg-send-btn">
          <Send className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
